<template>
  <div>
    <div class="group-rounded-box row-py-clear">
      <div class="row">
        <div class="col-12">
          <h3 class="mt-0">Channels</h3>
          <p class="mb-0">Select the channels that this customer is enabled for using the toggles below.</p>
        </div>
      </div>
      <div class="row">
        <div class="col-4">
          <toggle :heading="'Ex-Rack'" :boolValue="spotEnabled" :type="'spot'" @emitToggle="toggleValue" />
        </div>
        <div class="col-4">
          <toggle :heading="'Bunkering'" :boolValue="bunkeringEnabled" :type="'bunkering'" @emitToggle="toggleValue" />
        </div>
        <div class="col-4">
          <toggle :heading="'Delivered In'" :boolValue="dinEnabled" :type="'deliveredin'" @emitToggle="toggleValue" />
        </div>
      </div>
      <div class="row mt-5">
        <div class="col-12">
          <h3 class="mt-0">Terminals & Product Restrictions</h3>
          <p class="mb-4">
            Select the terminals and products that this customer is enabled for using the toggles below. Terminal and
            product restrictions will apply to both Ex-Rack and Delivered In offers. Any restrictions will not be
            applied to contract offers that you have published to this customer.
          </p>
        </div>
        <div class="col-12 mb-3">
          <input
            class="search-bar no-focus"
            :class="!$breakpoint.smAndDown ? 'w-25' : 'w-100'"
            v-model="searchInput"
            type="search"
            placeholder="Search terminals"
          />
        </div>
      </div>
      <div class="row" style="overflow-y: auto; height: 280px">
        <div class="col-12">
          <div class="card mb-1" v-for="(terminal, idx) in filteredTerminalsList" :key="idx">
            <div class="card-header bg-white">
              <div class="d-flex">
                <label class="switch">
                  <input
                    type="checkbox"
                    class="on-off-toggler"
                    :checked="terminal.terminalEnabled"
                    @click.stop="toggleValue('terminal', !terminal.terminalEnabled, terminal._id)"
                    :disabled="disableTerminalList"
                  />
                  <span class="slider round"></span>
                </label>
                <div
                  style="cursor: pointer"
                  class="flex-grow-1"
                  data-toggle="collapse"
                  :data-target="`#collapse${idx}`"
                >
                  <h4 class="text-align-left mt-1">{{ terminal.name }}</h4>
                </div>
                <div
                  style="cursor: pointer"
                  class="caret-animation collapsed"
                  data-toggle="collapse"
                  :data-target="`#collapse${idx}`"
                >
                  <h4 class="d-inline ml-1">
                    {{ enabledProductsSummary(terminal) }}
                  </h4>
                </div>
              </div>
            </div>
            <div :id="`collapse${idx}`" class="collapse">
              <div card="card-body">
                <div class="row text-center py-0">
                  <div class="col-4 py-2" v-for="fuel in terminal.fuels" :key="fuel.fuelId">
                    <span>
                      {{ fuel.fuelName }}
                      <input
                        :key="fuel._id"
                        class="float-right ml-0"
                        :disabled="disableTerminalList || !terminal.terminalEnabled"
                        type="checkbox"
                        v-model="fuel.enabled"
                    /></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer justify-content-center">
        <el-button class="btn btn-neutral" size="small" @click="emitClose"> Close </el-button>
        <el-button class="btn btn-neutral" size="small" @click="emitBack"> Back </el-button>
        <el-button class="btn btn-neutral" size="small" @click="emitNext" :disabled="tabIndex >= tabsLength">
          Next
        </el-button>
        <el-button
          class="btn btn-confirm"
          size="small"
          @click.prevent="save"
          :disabled="!unsavedChangesFound"
          :loading="saveBtnLoading"
        >
          Save
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
const deepEqual = require("fast-deep-equal");
import Toggle from "./Toggle.vue";
import { mapGetters } from "vuex";
import { editCustomerRelationship } from "@/api/relationships.js";
import breakpoint from "@/mixins/index";

export default {
  mixins: [breakpoint],
  data() {
    return {
      saveBtnLoading: false,
      searchInput: "",
      spotEnabled: true,
      bunkeringEnabled: true,
      dinEnabled: true,
      terminalsList: [],
      initialState: null,
    };
  },
  props: {
    relationshipProp: {
      type: Object,
      default: () => {},
      required: true,
    },
    tabsLength: {
      type: Number,
      default: 0,
      required: true,
    },
    tabIndex: {
      type: Number,
      default: 0,
      required: true,
    },
  },
  mounted() {
    this.spotEnabled = this.relationshipProp.enabled_spot;
    this.bunkeringEnabled = this.relationshipProp.enabled_bunkering;
    this.dinEnabled = this.relationshipProp.enabled_deliveredin;

    if (this.relationshipProp.restrictions && this.relationshipProp.restrictions.terminals.length > 1) {
      this.terminalsList = this.createTerminalsListFromRestrictions(
        this.getTerminalChanges,
        this.relationshipProp.restrictions.terminals
      );
    } else {
      this.terminalsList = this.createTerminalsListFromTerminals(this.getTerminalChanges);
    }

    this.initialState = {
      spotEnabled: this.spotEnabled,
      bunkeringEnabled: this.bunkeringEnabled,
      dinEnabled: this.dinEnabled,
      terminalsList: JSON.parse(JSON.stringify(this.terminalsList)),
    };
  },
  components: {
    Toggle,
  },
  computed: {
    ...mapGetters("terminals", ["getTerminalChanges"]),
    ...mapGetters("metadata", ["productConfig"]),
    ...mapGetters("fuelsType", ["getFuelsType"]),
    getFilteredFuels() {
      return this.getFuelsType.filter((f) => {
        if (Object.values(this.productConfig).indexOf(f._id) > -1 && f.type !== "NOT4SALE") {
          return f;
        }
      });
    },
    unsavedChangesFound: function () {
      const status =
        this.spotEnabled !== (this.initialState && this.initialState.spotEnabled) ||
        this.bunkeringEnabled !== (this.initialState && this.initialState.bunkeringEnabled) ||
        this.dinEnabled !== (this.initialState && this.initialState.dinEnabled) ||
        !deepEqual(this.initialState && this.initialState.terminalsList, this.terminalsList);
      this.$emit("emit-tracked-changes", status);
      return status;
    },
    filteredTerminalsList: function () {
      return this.terminalsList.filter((term) => term.name.toLowerCase().includes(this.searchInput.toLowerCase()));
    },
    disableTerminalList: function () {
      return !this.spotEnabled && !this.dinEnabled;
    },
  },
  watch: {
    unsavedChangesFound() {},
  },
  methods: {
    async save() {
      const payload = {
        relationshipId: this.relationshipProp.relationshipId,
        buyerAccountId: this.relationshipProp.id,
        spotEnabled: this.spotEnabled,
        bunkeringEnabled: this.bunkeringEnabled,
        dinEnabled: this.dinEnabled,
        restrictions: {
          terminalSelection: "MANUAL",
          terminals: this.terminalsList.map(({ _id, terminalEnabled, fuels }) => {
            return {
              terminal_id: _id,
              status: terminalEnabled ? "ENABLED" : "DISABLED",
              products: fuels.map((f) => {
                return {
                  product_id: f.fuelId,
                  status: f.enabled ? "ENABLED" : "DISABLED",
                };
              }),
            };
          }),
        },
      };
      this.saveBtnLoading = true;
      await editCustomerRelationship(payload);
      this.saveBtnLoading = false;
      this.$emit("reloadRelationshipProp");
    },
    toggleValue(type, boolValue, terminalId) {
      switch (type) {
        case "bunkering":
          this.bunkeringEnabled = boolValue;
          break;
        case "spot":
          this.spotEnabled = boolValue;
          break;
        case "deliveredin":
          this.dinEnabled = boolValue;
          break;
        case "terminal":
          this.terminalsList.find(({ _id }) => _id === terminalId).terminalEnabled = boolValue;
          break;
      }
    },
    emitNext() {
      this.$emit("emit-next", this.tabIndex);
    },
    emitBack() {
      this.$emit("emit-back", this.tabIndex);
    },
    emitClose() {
      this.$emit("emit-close", this.tabIndex);
    },
    createTerminalsListFromTerminals(terminals) {
      return terminals.map((terminal) => {
        return {
          ...terminal,
          terminalEnabled: true,
          fuels: this.getFilteredFuels.map((fuel) => {
            return {
              fuelName: fuel.name,
              fuelId: fuel._id,
              enabled: true,
            };
          }),
        };
      });
    },
    createTerminalsListFromRestrictions(terminals, relationshipRestrictions) {
      return terminals.map((terminal) => {
        const currentTerminalRestriction =
          relationshipRestrictions.find((restriction) => restriction.terminal_id === terminal._id) || {};
        return {
          ...terminal,
          terminalEnabled: this.isEnabled(currentTerminalRestriction.status),
          fuels: this.getFilteredFuels.map((fuel) => {
            const matchingFuel = currentTerminalRestriction.products
              ? currentTerminalRestriction.products.find(({ product_id }) => product_id === fuel._id)
              : null;
            return {
              fuelName: fuel.name,
              fuelId: fuel._id,
              enabled: matchingFuel ? (matchingFuel.status === "ENABLED" ? true : false) : false,
            };
          }),
        };
      });
    },
    isEnabled(equalsEnabled) {
      return equalsEnabled === "ENABLED";
    },
    enabledProductsSummary(terminal) {
      const totalNoOfProducts = terminal.fuels.length;
      const totalNoOfActiveProducts = terminal.fuels.filter((fuel) => fuel.enabled).length;
      return `${totalNoOfActiveProducts}/${totalNoOfProducts}`;
    },
  },
};
</script>
<style lang="scss" scoped>
.caret-animation {
  &[data-toggle="collapse"]:before {
    display: inline-block;
    font-family: "Font Awesome 5 Free";
    font-weight: bold;
    font-size: 18px;
    content: "\f0d7";
    transform: rotate(-180deg);
    transition: all linear 0.25s;
  }
  &[data-toggle="collapse"].collapsed:before {
    transform: rotate(0deg);
  }
}
</style>
